import { useState, useEffect } from 'react';
import { useLogicModel, ImpactGoal } from '../../../contexts/LogicModelContext';
import { supabase } from '../../../lib/supabase';
import { useTenant } from '../../../hooks/useTenant';

export function ImpactGoalStep() {
  const { logicModel, updateImpactGoals, markStepComplete } = useLogicModel();
  const { organization } = useTenant();
  const [goals, setGoals] = useState<ImpactGoal[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    timeframe: '7-10 years',
    contribution_statement: '',
  });

  useEffect(() => {
    if (logicModel?.impact_goals) {
      setGoals(logicModel.impact_goals);
    }
  }, [logicModel?.impact_goals]);

  // Mark step complete when we have at least one goal
  useEffect(() => {
    if (goals.length > 0) {
      markStepComplete('impact');
    }
  }, [goals.length, markStepComplete]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      timeframe: '7-10 years',
      contribution_statement: '',
    });
    setEditingId(null);
    setIsAdding(false);
  };

  const handleEdit = (goal: ImpactGoal) => {
    setFormData({
      name: goal.name,
      description: goal.description || '',
      timeframe: goal.timeframe || '7-10 years',
      contribution_statement: goal.contribution_statement || '',
    });
    setEditingId(goal.id || null);
    setIsAdding(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Impact goal name is required');
      return;
    }

    if (!logicModel?.id || !organization) return;

    setSaving(true);
    setError(null);

    try {
      if (editingId) {
        // Update existing
        const { error: updateError } = await supabase
          .from('impact_goals')
          .update({
            name: formData.name,
            description: formData.description || null,
            timeframe: formData.timeframe || null,
            contribution_statement: formData.contribution_statement || null,
          })
          .eq('id', editingId);

        if (updateError) throw updateError;

        const updatedGoals = goals.map(g =>
          g.id === editingId ? { ...g, ...formData } : g
        );
        setGoals(updatedGoals);
        updateImpactGoals(updatedGoals);
      } else {
        // Create new
        const { data: newGoal, error: insertError } = await supabase
          .from('impact_goals')
          .insert({
            logic_model_id: logicModel.id,
            organization_id: organization.organization_id,
            name: formData.name,
            description: formData.description || null,
            timeframe: formData.timeframe || null,
            contribution_statement: formData.contribution_statement || null,
            sort_order: goals.length,
          })
          .select()
          .single();

        if (insertError) throw insertError;

        const newGoals = [...goals, newGoal];
        setGoals(newGoals);
        updateImpactGoals(newGoals);
      }

      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (goalId: string) => {
    if (!confirm('Delete this impact goal?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('impact_goals')
        .delete()
        .eq('id', goalId);

      if (deleteError) throw deleteError;

      const updatedGoals = goals.filter(g => g.id !== goalId);
      setGoals(updatedGoals);
      updateImpactGoals(updatedGoals);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          🎯 Define Your Impact Goal
        </h2>
        <p className="text-gray-600">
          Start with the end in mind. What long-term transformation do you want to see in your community or the people you serve? Impact goals typically take 7-10 years to achieve.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">💡 Tips for Impact Goals</h3>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Think big — impact is about systemic or community-level change</li>
          <li>Be specific about WHO is affected and HOW</li>
          <li>Use a "contribution statement" to describe how your program helps achieve this</li>
          <li>Most programs have 1-2 impact goals</li>
        </ul>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Existing Goals */}
      {goals.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-medium text-gray-900">Your Impact Goals ({goals.length})</h3>
          {goals.map((goal) => (
            <div
              key={goal.id}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{goal.name}</h4>
                  {goal.description && (
                    <p className="text-sm text-gray-600 mt-1">{goal.description}</p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {goal.timeframe && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        ⏱️ {goal.timeframe}
                      </span>
                    )}
                  </div>
                  {goal.contribution_statement && (
                    <p className="text-sm text-gray-500 mt-2 italic">
                      "{goal.contribution_statement}"
                    </p>
                  )}
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => handleEdit(goal)}
                    className="text-gray-400 hover:text-blue-600"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => goal.id && handleDelete(goal.id)}
                    className="text-gray-400 hover:text-red-600"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form */}
      {isAdding ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <h3 className="font-medium text-gray-900">
            {editingId ? 'Edit Impact Goal' : 'Add Impact Goal'}
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Impact Goal Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="e.g., Economic mobility in underserved communities"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Describe the long-term change you want to see"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timeframe
            </label>
            <select
              value={formData.timeframe}
              onChange={(e) => setFormData({ ...formData, timeframe: e.target.value })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="5-7 years">5-7 years</option>
              <option value="7-10 years">7-10 years</option>
              <option value="10+ years">10+ years</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contribution Statement
            </label>
            <textarea
              value={formData.contribution_statement}
              onChange={(e) => setFormData({ ...formData, contribution_statement: e.target.value })}
              rows={2}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="How does YOUR program contribute to this impact? (Be careful not to overclaim)"
            />
            <p className="text-xs text-gray-500 mt-1">
              Tip: Use language like "contributes to" rather than "causes" or "achieves"
            </p>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={resetForm}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingId ? 'Update' : 'Add Goal'}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsAdding(true)}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
        >
          + Add Impact Goal
        </button>
      )}

      {/* Example impact goals */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-3">📋 Example Impact Goals</h3>
        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex items-start">
            <span className="text-green-500 mr-2">•</span>
            <span>"Economic mobility in underserved communities" — Workforce Development</span>
          </div>
          <div className="flex items-start">
            <span className="text-green-500 mr-2">•</span>
            <span>"Improved educational outcomes for first-generation students" — Education</span>
          </div>
          <div className="flex items-start">
            <span className="text-green-500 mr-2">•</span>
            <span>"Reduced food insecurity in rural areas" — Food Security</span>
          </div>
        </div>
      </div>
    </div>
  );
}
